import { useEffect, useRef, useState, type ReactNode } from "react";
import type { SurfLayer, SurfSlide, SurfStyle, TextRole } from "@vida/surf-core";
import {
  SURF_FONT_OPTIONS,
  addChartPoint,
  addChartSeries,
  addGraphicItem,
  addTableColumn,
  addTableRow,
  clearTextOverride,
  deckBackgroundColor,
  slideBackdropColor,
  getLayerChart,
  getLayerCrop,
  getLayerGraphic,
  getLayerTable,
  getLayerTextRender,
  getLayerBakedText,
  normalizeBakedText,
  type SurfTextRenderMode,
  moveGraphicItem,
  normalizeGraphicData,
  getTextOverride,
  getTextRenderMode,
  isTextRasterLayer,
  isTextRasterStale,
  normalizeChartData,
  setTextRenderMode,
  type TextRasterRenderMode,
  getRasterKind,
  setRasterKind,
  SURF_RASTER_KINDS,
  getRasterBackgroundMode,
  setRasterBackgroundMode,
  type RasterBackgroundMode,
  removeChartPoint,
  removeChartSeries,
  removeGraphicItem,
  removeTableColumn,
  removeTableRow,
  resolveTextStyle,
  resolveSlideVisualPrompt,
  styleColorPalette,
  styleTextPalette,
  normalizeTableData,
  setChartPoint,
  setChartType,
  setGraphicItem,
  setGraphicTemplate,
  setSeriesLabel,
  setTableCell,
  setTextOverride,
  type SurfChartType,
  type SurfGraphicTemplate,
  SURF_GRAPHIC_ICON_IDS,
} from "@vida/surf-core";

type Props = {
  layer: SurfLayer | null;
  slide?: SurfSlide | null;
  slideIndex?: number;
  theme: SurfStyle;
  onPatch: (patch: Partial<SurfLayer>) => void;
  onSlideBackgroundPromptChange?: (prompt: string) => void;
  onSlideBackgroundColorChange?: (hex: string | undefined) => void;
  onImageSubjectChange?: (subject: string) => void;
  onZOrder: (mode: "forward" | "backward" | "front" | "back") => void;
  onDelete: () => void;
  onDuplicate: () => void;
  legibilityStrategy: string;
  onLegibility: (strategy: "scrim" | "box" | "safezone" | "autoContrast") => void;
  imageEditBusy?: boolean;
  onImageRefineInstructions?: (instructions: string) => void;
  onEditImageLayer?: (instructions: string) => void;
  onUploadImageReference?: (file: File) => void;
  onUploadImageFromDisk?: (file: File) => void;
  onOpenImageCrop?: () => void;
  onResetImageCrop?: () => void;
  onBackgroundRefineInstructions?: (instructions: string) => void;
  onEditBackground?: (instructions: string) => void;
  onUploadBackgroundReference?: (file: File) => void;
  onSeedBakedTextFromSlide?: () => void;
  onGenerateTextRaster?: () => void;
  onEditTextRaster?: (instructions: string) => void;
  onUploadTextRasterReference?: (file: File) => void;
  onTextRasterRefineInstructions?: (instructions: string) => void;
};

const TEXT_ROLES: TextRole[] = ["title", "subtitle", "body", "bullet", "caption", "kicker"];

function ColorField({
  label,
  value,
  palette,
  onChange,
  allowNone,
  noneLabel = "Nessuno",
}: {
  label: string;
  value?: string;
  palette: string[];
  onChange: (hex: string | undefined) => void;
  allowNone?: boolean;
  noneLabel?: string;
}) {
  // Local draft for the native color input: update live during drag, but only
  // commit to the deck on release (change/blur) or after a short debounce —
  // avoids a deck commit on every pointer move.
  const [draft, setDraft] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const current = draft ?? (value && /^#[0-9a-fA-F]{6}$/.test(value) ? value : "#ffffff");

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const commit = (hex: string | undefined) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setDraft(null);
    onChange(hex);
  };

  return (
    <div className="field-label color-field">
      <span>{label}</span>
      <div className="color-field-swatches">
        {palette.map((hex) => (
          <button
            key={hex}
            type="button"
            className={`color-swatch${value?.toUpperCase() === hex.toUpperCase() ? " active" : ""}`}
            style={{ background: hex }}
            title={hex}
            aria-label={hex}
            onClick={() => commit(hex)}
          />
        ))}
        <input
          type="color"
          value={current}
          aria-label={`${label} personalizzato`}
          // Chrome fires BOTH input and change continuously while dragging → never
          // commit on those (would re-render the widget every move). Only update the
          // local preview + debounce; the real commit happens on blur (picker close).
          onInput={(e) => {
            const hex = (e.target as HTMLInputElement).value;
            setDraft(hex);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => commit(hex), 600);
          }}
          onChange={(e) => {
            const hex = (e.target as HTMLInputElement).value;
            setDraft(hex);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => commit(hex), 600);
          }}
          onBlur={() => {
            if (draft) commit(draft);
          }}
        />
        {allowNone && (
          <button type="button" className="surf-btn surf-btn--ghost" onClick={() => commit(undefined)}>
            {noneLabel}
          </button>
        )}
      </div>
      {(draft ?? value) && <code>{(draft ?? value)!.toUpperCase()}</code>}
    </div>
  );
}

export function LayerInspector({
  layer,
  slide,
  slideIndex = 0,
  theme,
  onPatch,
  onSlideBackgroundPromptChange,
  onSlideBackgroundColorChange,
  onImageSubjectChange,
  onZOrder,
  onDelete,
  onDuplicate,
  legibilityStrategy,
  onLegibility,
  imageEditBusy,
  onImageRefineInstructions,
  onEditImageLayer,
  onUploadImageReference,
  onUploadImageFromDisk,
  onOpenImageCrop,
  onResetImageCrop,
  onBackgroundRefineInstructions,
  onEditBackground,
  onUploadBackgroundReference,
  onSeedBakedTextFromSlide,
  onGenerateTextRaster,
  onEditTextRaster,
  onUploadTextRasterReference,
  onTextRasterRefineInstructions,
}: Props) {
  const imageRefInput = useRef<HTMLInputElement>(null);
  const imageDiskInput = useRef<HTMLInputElement>(null);
  const bgRefInput = useRef<HTMLInputElement>(null);
  const textRasterRefInput = useRef<HTMLInputElement>(null);

  const backgroundHasImage = Boolean(slide?.background.s3Key?.trim());
  const imageLayerHasBitmap = layer?.type === "image" && Boolean(layer.content?.trim());
  if (!layer) {
    return (
      <aside className="editor-inspector">
        <header className="inspector-head">
          <h3>Layer</h3>
        </header>
        <p className="muted inspector-empty">Seleziona un layer sulla slide per modificarne proprietà e stile.</p>
        {slide && (
          <SlideBackgroundSection
            slide={slide}
            slideIndex={slideIndex}
            theme={theme}
            hasBitmap={backgroundHasImage}
            busy={imageEditBusy}
            fileInputRef={bgRefInput}
            onPromptChange={(prompt) => onSlideBackgroundPromptChange?.(prompt)}
            onColorChange={(hex) => onSlideBackgroundColorChange?.(hex)}
            onInstructionsChange={onBackgroundRefineInstructions}
            onEdit={onEditBackground}
            onPickReference={() => bgRefInput.current?.click()}
            onReferenceFile={(file) => onUploadBackgroundReference?.(file)}
          />
        )}
        <h4>Leggibilità slide</h4>
        <LegibilityToggle strategy={legibilityStrategy} onChange={onLegibility} />
      </aside>
    );
  }

  const textOverride = layer.type === "text" ? getTextOverride(layer) : {};
  const resolved = layer.type === "text" ? resolveTextStyle(layer, theme) : null;
  const hasOverride = layer.type === "text" && Object.keys(textOverride).length > 0;

  const patchText = (patch: Parameters<typeof setTextOverride>[1]) => {
    onPatch({ style: setTextOverride(layer, patch).style });
  };

  return (
    <aside className="editor-inspector">
      <header className="inspector-head">
        <h3>Layer · {layer.type}</h3>
        <div className="inspector-quick-actions">
          <button type="button" className="surf-btn surf-btn--ghost" onClick={onDuplicate}>
            Duplica
          </button>
          <button type="button" className="danger-link" onClick={onDelete}>
            Elimina
          </button>
        </div>
      </header>
      {layer.type === "text" && (
        <>
          <InspectorSection title="Contenuto" defaultOpen>
            <label className="field-label">
              Contenuto
              <textarea
                className="inspector-textarea"
                rows={layer.textRole === "bullet" || layer.textRole === "body" ? 5 : 2}
                value={layer.content ?? ""}
                placeholder="Testo del widget"
                onChange={(e) => onPatch({ content: e.target.value })}
              />
            </label>

            <label className="field-label">
              Ruolo testo
              <select
                value={layer.textRole ?? "body"}
                onChange={(e) => onPatch({ textRole: e.target.value as TextRole })}
              >
                {TEXT_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>

            <label className="field-label">
              Modalità render
              <select
                value={getTextRenderMode(layer)}
                onChange={(e) =>
                  onPatch({
                    style: setTextRenderMode(layer, e.target.value as TextRasterRenderMode).style,
                  })
                }
              >
                <option value="vector">Testo vettoriale</option>
                <option value="raster">Illustrazione (raster)</option>
              </select>
            </label>
          </InspectorSection>

          {getTextRenderMode(layer) === "vector" && (
            <InspectorSection title="Tipografia">
              <label className="field-label">
                Font
                <select
                  value={resolved?.font ?? "Surf Sans"}
                  onChange={(e) => patchText({ font: e.target.value })}
                >
                  {SURF_FONT_OPTIONS.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field-label">
                Peso ({resolved?.weight ?? 400})
                <input
                  type="range"
                  min={300}
                  max={800}
                  step={100}
                  value={resolved?.weight ?? 400}
                  onChange={(e) => patchText({ weight: Number(e.target.value) })}
                />
              </label>
              <label className="field-label">
                Dimensione rel. ({(resolved?.sizeRel ?? 0.035).toFixed(3)})
                <input
                  type="range"
                  min={0.02}
                  max={0.12}
                  step={0.001}
                  value={resolved?.sizeRel ?? 0.035}
                  onChange={(e) => patchText({ sizeRel: Number(e.target.value) })}
                />
              </label>
              <ColorField
                label="Colore"
                value={textOverride.color?.startsWith("#") ? textOverride.color : undefined}
                palette={styleTextPalette(theme)}
                onChange={(hex) => patchText({ color: hex })}
                allowNone
                noneLabel="Da ruolo"
              />
              <label className="field-label">
                Allineamento
                <select
                  value={resolved?.align ?? "left"}
                  onChange={(e) => patchText({ align: e.target.value as "left" | "center" | "right" })}
                >
                  <option value="left">Sinistra</option>
                  <option value="center">Centro</option>
                  <option value="right">Destra</option>
                </select>
              </label>
              <label className="field-label">
                Interlinea ({(resolved?.lineHeight ?? 1.4).toFixed(2)})
                <input
                  type="range"
                  min={1}
                  max={2}
                  step={0.05}
                  value={resolved?.lineHeight ?? 1.4}
                  onChange={(e) => patchText({ lineHeight: Number(e.target.value) })}
                />
              </label>
              <label className="field-label">
                Tracking ({(resolved?.tracking ?? 0).toFixed(2)}em)
                <input
                  type="range"
                  min={-0.05}
                  max={0.2}
                  step={0.01}
                  value={resolved?.tracking ?? 0}
                  onChange={(e) => patchText({ tracking: Number(e.target.value) })}
                />
              </label>
              <label className="field-label">
                Maiuscole
                <select
                  value={resolved?.case ?? "none"}
                  onChange={(e) => patchText({ case: e.target.value as "none" | "upper" })}
                >
                  <option value="none">Normale</option>
                  <option value="upper">MAIUSCOLO</option>
                </select>
              </label>
              {hasOverride && (
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => onPatch({ style: clearTextOverride(layer).style })}
                >
                  Reset al ruolo
                </button>
              )}
            </InspectorSection>
          )}

          {getTextRenderMode(layer) !== "raster" && (
            <InspectorSection title="Sfondo riquadro">
              <ColorField
                label="Colore (overlay su sfondo colorato)"
                value={typeof layer.style?.boxBg === "string" ? layer.style.boxBg : undefined}
                palette={styleColorPalette(theme)}
                allowNone
                onChange={(hex) =>
                  onPatch({
                    style: {
                      ...layer.style,
                      boxBg: hex,
                      boxBgOpacity: hex
                        ? typeof layer.style?.boxBgOpacity === "number"
                          ? layer.style.boxBgOpacity
                          : 1
                        : undefined,
                    },
                  })
                }
              />
              {typeof layer.style?.boxBg === "string" && (
                <label className="field-label">
                  Opacità ({Math.round(((layer.style?.boxBgOpacity as number) ?? 1) * 100)}%)
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={(layer.style?.boxBgOpacity as number) ?? 1}
                    onChange={(e) =>
                      onPatch({ style: { ...layer.style, boxBgOpacity: Number(e.target.value) } })
                    }
                  />
                </label>
              )}
            </InspectorSection>
          )}

          {getTextRenderMode(layer) === "raster" && (
            <>
              <InspectorSection title="Illustrazione raster">
                <label className="field-label">
                  Tipo di artefatto
                  <select
                    value={getRasterKind(layer)}
                    onChange={(e) =>
                      onPatch({
                        style: setRasterKind(
                          layer,
                          e.target.value as (typeof SURF_RASTER_KINDS)[number]["id"],
                        ).style,
                      })
                    }
                  >
                    {SURF_RASTER_KINDS.map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.label}
                      </option>
                    ))}
                  </select>
                </label>
                <p className="muted inspector-hint">
                  {SURF_RASTER_KINDS.find((k) => k.id === getRasterKind(layer))?.description}
                </p>
                {getRasterKind(layer) === "chart" && (
                  <p className="muted inspector-hint">
                    Solo se il testo contiene numeri; altrimenti verrà generato un diagramma.
                  </p>
                )}
                {getRasterKind(layer) === "custom" && (
                  <label className="field-label">
                    Prompt personalizzato
                    <textarea
                      className="inspector-textarea"
                      rows={3}
                      placeholder="Descrivi cosa generare. Lo stile della presentazione viene comunque applicato."
                      value={
                        typeof layer.style?.rasterCustomPrompt === "string"
                          ? layer.style.rasterCustomPrompt
                          : ""
                      }
                      onChange={(e) =>
                        onPatch({
                          style: {
                            ...layer.style,
                            rasterCustomPrompt: e.target.value,
                            rasterStale: true,
                          },
                        })
                      }
                    />
                  </label>
                )}
                <ColorField
                  label="Colore sfondo illustrazione"
                  value={
                    typeof layer.style?.rasterBackdropOverride === "string"
                      ? layer.style.rasterBackdropOverride
                      : undefined
                  }
                  palette={styleColorPalette(theme)}
                  allowNone
                  onChange={(hex) =>
                    onPatch({
                      style: { ...layer.style, rasterBackdropOverride: hex, rasterStale: true },
                    })
                  }
                />
                <p className="muted inspector-hint">
                  Vuoto = colore di sfondo della slide. Scegli dalla palette o un esadecimale.
                </p>
              <p className="muted inspector-hint baked-fidelity-warning">
                Il modello può sbagliare ortografia e diacritici nel testo cotto nell&apos;immagine.
                Il testo in <code>content</code> resta la fonte canonica — puoi tornare al vettoriale
                in qualsiasi momento.
              </p>
              {isTextRasterStale(layer) && (
                <p className="text-raster-stale-hint">Testo modificato — rigenera l&apos;illustrazione.</p>
              )}
              <div className="wizard-actions">
                <button
                  type="button"
                  className="primary-btn"
                  disabled={imageEditBusy}
                  onClick={() => onGenerateTextRaster?.()}
                >
                  {isTextRasterLayer(layer) ? "Rigenera illustrazione" : "Genera illustrazione"}
                </button>
              </div>
              <ImageRefineBlock
                title="Modifica illustrazione raster"
                instructions={
                  typeof layer.style?.refineInstructions === "string"
                    ? layer.style.refineInstructions
                    : ""
                }
                referenceKey={
                  typeof layer.style?.imageReferenceKey === "string"
                    ? layer.style.imageReferenceKey
                    : undefined
                }
                hasBitmap={Boolean(layer.style?.rasterAssetKey)}
                busy={imageEditBusy}
                fileInputRef={textRasterRefInput}
                onInstructionsChange={onTextRasterRefineInstructions}
                onEdit={onEditTextRaster}
                onPickReference={() => textRasterRefInput.current?.click()}
                onReferenceFile={(file) => onUploadTextRasterReference?.(file)}
              />
              </InspectorSection>

              <InspectorSection title="Ritaglio">
                {getLayerCrop(layer) && (
                  <p>
                    <span className="image-crop-badge">Ritagliata</span>
                  </p>
                )}
                <div className="table-inspector-actions">
                  <button
                    type="button"
                    className="primary-btn"
                    disabled={!layer.style?.rasterAssetKey}
                    onClick={() => onOpenImageCrop?.()}
                  >
                    Ritaglia
                  </button>
                  {getLayerCrop(layer) && (
                    <button type="button" onClick={() => onResetImageCrop?.()}>
                      Reset ritaglio
                    </button>
                  )}
                </div>
                {!layer.style?.rasterAssetKey && (
                  <p className="muted inspector-hint">
                    Genera l&apos;illustrazione raster per abilitare il ritaglio.
                  </p>
                )}
              </InspectorSection>

              <InspectorSection title="Sfondo immagine">
                <RasterBackgroundSelect
                  layer={layer}
                  slide={slide}
                  theme={theme}
                  onModeChange={(mode) =>
                    onPatch({ style: setRasterBackgroundMode(layer, mode).style })
                  }
                />
              </InspectorSection>
            </>
          )}
        </>
      )}
      {layer.type === "image" && (
        <>
          <h4>Immagine widget</h4>
          <label className="field-label">
            Modalità testo
            <select
              value={getLayerTextRender(layer)}
              onChange={(e) =>
                onPatch({
                  style: {
                    ...layer.style,
                    textRender: e.target.value as SurfTextRenderMode,
                  },
                })
              }
            >
              <option value="overlay">Overlay (testo come widget)</option>
              <option value="in_image">Nell&apos;immagine (AI baked)</option>
            </select>
          </label>
          {getLayerTextRender(layer) === "in_image" && (
            <BakedIllustrationFields
              layer={layer}
              onPatch={onPatch}
              onSeedFromSlide={onSeedBakedTextFromSlide}
            />
          )}
          {getLayerTextRender(layer) === "overlay" && (
            <label className="field-label">
              Soggetto (opzionale)
              <textarea
                className="inspector-textarea"
                rows={3}
                value={
                  typeof layer.style?.imageSubject === "string" ? layer.style.imageSubject : ""
                }
                placeholder="Vuoto = soggetto da contesto slide (brief o titolo)"
                onChange={(e) => onImageSubjectChange?.(e.target.value)}
              />
            </label>
          )}
          {getLayerTextRender(layer) === "overlay" ? (
            <p className="muted inspector-hint">
              Genera con AI dalla toolbar («Genera immagine widget») oppure carica un file locale.
            </p>
          ) : (
            <p className="muted inspector-hint baked-fidelity-warning">
              Il modello può sbagliare ortografia e diacritici nel testo cotto. Dopo la generazione il
              testo non è editabile nel pixel — solo rigenera. Export: immagine piatta (F14).
            </p>
          )}
          <div className="table-inspector-actions">
            <button
              type="button"
              className="primary-btn"
              disabled={imageEditBusy}
              onClick={() => imageDiskInput.current?.click()}
            >
              Carica da disco
            </button>
          </div>
          <input
            ref={imageDiskInput}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUploadImageFromDisk?.(file);
              e.target.value = "";
            }}
          />
          <InspectorSection title="Ritaglio" defaultOpen>
            {getLayerCrop(layer) && (
              <p>
                <span className="image-crop-badge">Ritagliata</span>
              </p>
            )}
            <div className="table-inspector-actions">
              <button
                type="button"
                className="primary-btn"
                disabled={!imageLayerHasBitmap}
                onClick={() => onOpenImageCrop?.()}
              >
                Ritaglia
              </button>
              {getLayerCrop(layer) && (
                <button type="button" onClick={() => onResetImageCrop?.()}>
                  Reset ritaglio
                </button>
              )}
            </div>
            {!imageLayerHasBitmap && (
              <p className="muted inspector-hint">
                Genera o carica un&apos;immagine per abilitare il ritaglio.
              </p>
            )}
          </InspectorSection>
          <InspectorSection title="Sfondo immagine">
            <RasterBackgroundSelect
              layer={layer}
              slide={slide}
              theme={theme}
              onModeChange={(mode) =>
                onPatch({ style: setRasterBackgroundMode(layer, mode).style })
              }
            />
          </InspectorSection>
          <ImageRefineBlock
            title="Modifica immagine"
            instructions={
              typeof layer.style?.refineInstructions === "string"
                ? layer.style.refineInstructions
                : ""
            }
            referenceKey={
              typeof layer.style?.imageReferenceKey === "string"
                ? layer.style.imageReferenceKey
                : undefined
            }
            hasBitmap={imageLayerHasBitmap}
            busy={imageEditBusy}
            fileInputRef={imageRefInput}
            onInstructionsChange={onImageRefineInstructions}
            onEdit={onEditImageLayer}
            onPickReference={() => imageRefInput.current?.click()}
            onReferenceFile={(file) => onUploadImageReference?.(file)}
          />
        </>
      )}
      {layer.type === "table" && (
        <TableInspector
          layer={layer}
          onTableChange={(table) =>
            onPatch({ style: { ...layer.style, table: normalizeTableData(table) } })
          }
        />
      )}
      {layer.type === "chart" && (
        <ChartInspector
          layer={layer}
          onChartChange={(chart) =>
            onPatch({ style: { ...layer.style, chart: normalizeChartData(chart) } })
          }
        />
      )}
      {layer.type === "graphic" && (
        <GraphicInspector
          layer={layer}
          onGraphicChange={(graphic) =>
            onPatch({ style: { ...layer.style, graphic: normalizeGraphicData(graphic) } })
          }
        />
      )}
      {slide && layer.type !== "image" && (
        <SlideBackgroundSection
          slide={slide}
          slideIndex={slideIndex}
          theme={theme}
          hasBitmap={backgroundHasImage}
          busy={imageEditBusy}
          fileInputRef={bgRefInput}
          onPromptChange={(prompt) => onSlideBackgroundPromptChange?.(prompt)}
          onColorChange={(hex) => onSlideBackgroundColorChange?.(hex)}
          onInstructionsChange={onBackgroundRefineInstructions}
          onEdit={onEditBackground}
          onPickReference={() => bgRefInput.current?.click()}
          onReferenceFile={(file) => onUploadBackgroundReference?.(file)}
        />
      )}
      <label className="field-label">
        Rotazione (°)
        <input
          type="number"
          min={-180}
          max={180}
          value={Math.round(layer.rotation)}
          onChange={(e) => onPatch({ rotation: Number(e.target.value) || 0 })}
        />
      </label>
      <div className="wizard-actions">
        <button type="button" onClick={() => onZOrder("forward")}>
          Avanti
        </button>
        <button type="button" onClick={() => onZOrder("backward")}>
          Indietro
        </button>
        <button type="button" onClick={() => onZOrder("front")}>
          In cima
        </button>
        <button type="button" onClick={() => onZOrder("back")}>
          In fondo
        </button>
      </div>
      <div className="wizard-actions">
        <button type="button" onClick={onDuplicate}>
          Duplica
        </button>
        <button type="button" className="danger-link" onClick={onDelete}>
          Elimina
        </button>
      </div>
      <InspectorSection title="Leggibilità slide">
        <LegibilityToggle strategy={legibilityStrategy} onChange={onLegibility} />
      </InspectorSection>
    </aside>
  );
}

function TableInspector({
  layer,
  onTableChange,
}: {
  layer: SurfLayer;
  onTableChange: (table: ReturnType<typeof getLayerTable>) => void;
}) {
  const data = getLayerTable(layer);

  const patch = (next: ReturnType<typeof getLayerTable>) => onTableChange(next);

  return (
    <>
      <h4>Tabella</h4>
      <div className="table-inspector-actions">
        <button type="button" onClick={() => patch(addTableColumn(data))}>
          + Colonna
        </button>
        <button type="button" onClick={() => patch(addTableRow(data))}>
          + Riga
        </button>
      </div>
      <div className="table-inspector-grid">
        <table>
          <thead>
            <tr>
              {data.columns.map((col, ci) => (
                <th key={`th-${ci}`}>
                  <input
                    className="text-input"
                    value={col}
                    onChange={(e) => patch(setTableCell(data, -1, ci, e.target.value))}
                  />
                  <button
                    type="button"
                    className="table-inspector-mini"
                    disabled={data.columns.length <= 1}
                    onClick={() => patch(removeTableColumn(data, ci))}
                    title="Rimuovi colonna"
                  >
                    ×
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, ri) => (
              <tr key={`tr-${ri}`}>
                {data.columns.map((_, ci) => (
                  <td key={`td-${ri}-${ci}`}>
                    <input
                      className="text-input"
                      value={row[ci] ?? ""}
                      onChange={(e) => patch(setTableCell(data, ri, ci, e.target.value))}
                    />
                  </td>
                ))}
                <td>
                  <button
                    type="button"
                    className="table-inspector-mini"
                    onClick={() => patch(removeTableRow(data, ri))}
                    title="Rimuovi riga"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ChartInspector({
  layer,
  onChartChange,
}: {
  layer: SurfLayer;
  onChartChange: (chart: ReturnType<typeof getLayerChart>) => void;
}) {
  const data = getLayerChart(layer);
  const patch = (next: ReturnType<typeof getLayerChart>) => onChartChange(next);

  return (
    <>
      <h4>Grafico</h4>
      <label className="field-label">
        Tipo
        <select
          value={data.type}
          onChange={(e) => patch(setChartType(data, e.target.value as SurfChartType))}
        >
          <option value="bar">Barre</option>
          <option value="line">Linea</option>
          <option value="pie">Torta</option>
        </select>
      </label>
      <div className="table-inspector-actions">
        <button type="button" onClick={() => patch(addChartSeries(data))}>
          + Serie
        </button>
      </div>
      {data.series.map((series, si) => (
        <div key={`series-${si}`} className="chart-series-block">
          <label className="field-label">
            Serie
            <input
              className="text-input"
              value={series.label}
              onChange={(e) => patch(setSeriesLabel(data, si, e.target.value))}
            />
          </label>
          <div className="table-inspector-actions">
            <button type="button" onClick={() => patch(addChartPoint(data, si))}>
              + Punto
            </button>
            <button
              type="button"
              disabled={data.series.length <= 1}
              onClick={() => patch(removeChartSeries(data, si))}
            >
              Rimuovi serie
            </button>
          </div>
          {series.points.map((point, pi) => (
            <div key={`pt-${si}-${pi}`} className="outline-bullet-row">
              <input
                className="text-input"
                value={point.x}
                placeholder="Categoria"
                onChange={(e) =>
                  patch(setChartPoint(data, si, pi, { x: e.target.value }))
                }
              />
              <input
                className="text-input chart-value-input"
                type="number"
                value={point.y}
                onChange={(e) =>
                  patch(setChartPoint(data, si, pi, { y: Number(e.target.value) || 0 }))
                }
              />
              <button
                type="button"
                className="table-inspector-mini"
                disabled={series.points.length <= 1}
                onClick={() => patch(removeChartPoint(data, si, pi))}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ))}
      <p className="muted inspector-hint">Valori solo da sorgente outline o input manuale.</p>
    </>
  );
}

function BakedIllustrationFields({
  layer,
  onPatch,
  onSeedFromSlide,
}: {
  layer: SurfLayer;
  onPatch: (patch: Partial<SurfLayer>) => void;
  onSeedFromSlide?: () => void;
}) {
  const baked = getLayerBakedText(layer);
  const patchBaked = (next: ReturnType<typeof getLayerBakedText>) =>
    onPatch({ style: { ...layer.style, bakedText: normalizeBakedText(next) } });

  return (
    <>
      <label className="field-label">
        Titolo da cuocere
        <input
          className="text-input"
          value={baked.title ?? ""}
          placeholder="Titolo slide"
          onChange={(e) => patchBaked({ ...baked, title: e.target.value })}
        />
      </label>
      <label className="field-label">
        Bullet (uno per riga)
        <textarea
          className="inspector-textarea"
          rows={4}
          value={(baked.bullets ?? []).join("\n")}
          placeholder="Punto uno&#10;Punto due"
          onChange={(e) =>
            patchBaked({
              ...baked,
              bullets: e.target.value.split("\n").map((l) => l.trim()).filter(Boolean),
            })
          }
        />
      </label>
      {onSeedFromSlide && (
        <button type="button" className="secondary-btn" onClick={onSeedFromSlide}>
          Copia testo da slide
        </button>
      )}
    </>
  );
}

function SlideBackgroundSection({
  slide,
  slideIndex,
  theme,
  hasBitmap,
  busy,
  fileInputRef,
  onPromptChange,
  onColorChange,
  onInstructionsChange,
  onEdit,
  onPickReference,
  onReferenceFile,
}: {
  slide: SurfSlide;
  slideIndex: number;
  theme: SurfStyle;
  hasBitmap: boolean;
  busy?: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onPromptChange: (prompt: string) => void;
  onColorChange?: (hex: string | undefined) => void;
  onInstructionsChange?: (instructions: string) => void;
  onEdit?: (instructions: string) => void;
  onPickReference?: () => void;
  onReferenceFile?: (file: File) => void;
}) {
  const [showResolved, setShowResolved] = useState(false);
  const resolved = resolveSlideVisualPrompt(theme, slide, slideIndex);

  return (
    <InspectorSection title="Sfondo slide" defaultOpen>
      {theme.derivedPrompt?.trim() ? (
        <p className="muted inspector-style-context">
          <strong>Stile (derivato):</strong> {theme.derivedPrompt}
        </p>
      ) : null}
      <ColorField
        label="Colore sfondo"
        value={slide.background.solidColor}
        palette={styleColorPalette(theme)}
        allowNone
        noneLabel="Da stile"
        onChange={(hex) => onColorChange?.(hex)}
      />
      {hasBitmap ? (
        <p className="muted inspector-hint">L&apos;immagine di sfondo ha priorità sul colore.</p>
      ) : slide.background.solidColor ? (
        <p className="muted inspector-hint">Colore pieno applicato (senza immagine).</p>
      ) : (
        <p className="muted inspector-hint">Senza colore: gradiente dalla palette dello stile.</p>
      )}
      <label className="field-label">
        Prompt immagine slide
        <textarea
          className="inspector-textarea"
          rows={4}
          value={slide.background.prompt ?? ""}
          placeholder={resolved}
          onChange={(e) => onPromptChange(e.target.value)}
        />
      </label>
      <div className="wizard-actions">
        <button type="button" onClick={() => setShowResolved((v) => !v)}>
          {showResolved ? "Nascondi prompt risolto" : "Mostra prompt risolto"}
        </button>
      </div>
      {showResolved ? <pre className="inspector-resolved-prompt muted">{resolved}</pre> : null}
      {hasBitmap ? (
        <ImageRefineBlock
          title="Modifica sfondo"
          instructions={slide.background.refineInstructions ?? ""}
          referenceKey={slide.background.referenceS3Key}
          hasBitmap={hasBitmap}
          busy={busy}
          fileInputRef={fileInputRef}
          onInstructionsChange={onInstructionsChange}
          onEdit={onEdit}
          onPickReference={onPickReference}
          onReferenceFile={onReferenceFile}
        />
      ) : null}
    </InspectorSection>
  );
}

function ImageRefineBlock({
  title,
  instructions,
  referenceKey,
  hasBitmap,
  busy,
  fileInputRef,
  onInstructionsChange,
  onEdit,
  onPickReference,
  onReferenceFile,
}: {
  title: string;
  instructions: string;
  referenceKey?: string;
  hasBitmap: boolean;
  busy?: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onInstructionsChange?: (v: string) => void;
  onEdit?: (instructions: string) => void;
  onPickReference?: () => void;
  onReferenceFile?: (file: File) => void;
}) {
  const [draft, setDraft] = useState(instructions);

  useEffect(() => {
    setDraft(instructions);
  }, [instructions]);

  const persistDraft = () => {
    if (draft !== instructions) onInstructionsChange?.(draft);
  };

  return (
    <div className="image-refine-block">
      <h4>{title}</h4>
      <label className="field-label">
        Istruzioni modifica
        <textarea
          className="inspector-textarea"
          rows={3}
          value={draft}
          placeholder="Es. tono più caldo, meno contrasto, aggiungi luce laterale…"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={persistDraft}
        />
      </label>
      <div className="table-inspector-actions">
        <button
          type="button"
          className="primary-btn"
          disabled={busy || !hasBitmap || !draft.trim()}
          onClick={() => {
            onInstructionsChange?.(draft);
            onEdit?.(draft);
          }}
        >
          Modifica
        </button>
        <button type="button" disabled={busy} onClick={() => onPickReference?.()}>
          Carica riferimento
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onReferenceFile?.(file);
          e.target.value = "";
        }}
      />
      {referenceKey ? (
        <p className="muted inspector-hint">Riferimento caricato (s3).</p>
      ) : (
        <p className="muted inspector-hint">
          Opzionale: carica un&apos;immagine stile/riferimento per guidare la modifica.
        </p>
      )}
      {!hasBitmap && (
        <p className="muted inspector-hint">Genera prima un&apos;immagine per abilitare Modifica.</p>
      )}
    </div>
  );
}

function GraphicInspector({
  layer,
  onGraphicChange,
}: {
  layer: SurfLayer;
  onGraphicChange: (graphic: ReturnType<typeof getLayerGraphic>) => void;
}) {
  const data = getLayerGraphic(layer);
  const patch = (next: ReturnType<typeof getLayerGraphic>) => onGraphicChange(next);

  return (
    <>
      <h4>Illustrazione</h4>
      <label className="field-label">
        Template
        <select
          value={data.template}
          onChange={(e) => patch(setGraphicTemplate(data, e.target.value as SurfGraphicTemplate))}
        >
          <option value="list">Lista</option>
          <option value="process">Processo</option>
          <option value="cycle">Ciclo</option>
          <option value="pyramid">Piramide</option>
          <option value="comparison">Confronto</option>
        </select>
      </label>
      <div className="table-inspector-actions">
        <button type="button" onClick={() => patch(addGraphicItem(data))}>
          + Elemento
        </button>
      </div>
      {data.items.map((item, i) => (
        <div key={`graphic-item-${i}`} className="chart-series-block">
          <label className="field-label">
            Etichetta
            <input
              className="text-input"
              value={item.label}
              onChange={(e) => patch(setGraphicItem(data, i, { label: e.target.value }))}
            />
          </label>
          <label className="field-label">
            Sottotitolo
            <input
              className="text-input"
              value={item.sublabel ?? ""}
              onChange={(e) => patch(setGraphicItem(data, i, { sublabel: e.target.value }))}
            />
          </label>
          <label className="field-label">
            Icona
            <select
              value={item.icon ?? "dot"}
              onChange={(e) => patch(setGraphicItem(data, i, { icon: e.target.value }))}
            >
              {SURF_GRAPHIC_ICON_IDS.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          </label>
          <div className="table-inspector-actions">
            <button type="button" disabled={i === 0} onClick={() => patch(moveGraphicItem(data, i, -1))}>
              ↑
            </button>
            <button
              type="button"
              disabled={i >= data.items.length - 1}
              onClick={() => patch(moveGraphicItem(data, i, 1))}
            >
              ↓
            </button>
            <button
              type="button"
              disabled={data.items.length <= 1}
              onClick={() => patch(removeGraphicItem(data, i))}
            >
              Rimuovi
            </button>
          </div>
        </div>
      ))}
      <p className="muted inspector-hint">Usa «Rigenera con AI» nella toolbar per ricomporre dal testo slide.</p>
    </>
  );
}

function InspectorSection({
  title,
  defaultOpen,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  return (
    <details className="inspector-section" open={defaultOpen}>
      <summary className="inspector-section-summary">{title}</summary>
      <div className="inspector-section-body">{children}</div>
    </details>
  );
}

function RasterBackgroundSelect({
  layer,
  slide,
  theme,
  onModeChange,
}: {
  layer: SurfLayer;
  slide?: SurfSlide | null;
  theme: SurfStyle;
  onModeChange: (mode: RasterBackgroundMode) => void;
}) {
  const slideHex = slide ? slideBackdropColor(slide, theme) : deckBackgroundColor(theme);
  const mode = getRasterBackgroundMode(layer);

  return (
    <label className="field-label">
      Sfondo immagine
      <select value={mode} onChange={(e) => onModeChange(e.target.value as RasterBackgroundMode)}>
        <option value="slide">Colore slide ({slideHex})</option>
        <option value="transparent">Trasparente</option>
        <option value="white">Bianco</option>
      </select>
      {mode === "slide" && (
        <span className="raster-bg-preview">
          <span
            className="bg-color-swatch"
            style={{
              display: "inline-block",
              width: "0.9rem",
              height: "0.9rem",
              borderRadius: 3,
              border: "1px solid var(--border, #334155)",
              background: slideHex,
            }}
            aria-hidden
          />
          <code>{slideHex}</code>
        </span>
      )}
    </label>
  );
}

function LegibilityToggle({
  strategy,
  onChange,
}: {
  strategy: string;
  onChange: (s: "scrim" | "box" | "safezone" | "autoContrast") => void;
}) {
  return (
    <div className="legibility-toggles">
      {(["scrim", "box", "safezone"] as const).map((s) => (
        <button
          key={s}
          type="button"
          className={strategy === s ? "step active" : "step"}
          onClick={() => onChange(s)}
        >
          {s}
        </button>
      ))}
    </div>
  );
}
